<template>
  <n-card title="Loan Management">
    <n-space vertical>
      <n-table :bordered="false" :single-line="false">
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>Amount</th>
            <th>Interest Rate</th>
            <th>Status</th>
            <th>Start Date</th>
            <th>End Date</th>
            <th>Remaining</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="loan in loans" :key="loan.id">
            <td>{{ loan.name }}</td>
            <td>
              <n-tag :type="getLoanTypeStyle(loan.type)">
                {{ loan.type }}
              </n-tag>
            </td>
            <td>{{ formatCurrency(loan.amount) }}</td>
            <td>{{ formatPercent(loan.interest_rate) }}</td>
            <td>
              <n-tag :type="getStatusStyle(loan.status)">
                {{ loan.status }}
              </n-tag>
            </td>
            <td>{{ formatDate(loan.start_date) }}</td>
            <td>{{ formatDate(loan.end_date) }}</td>
            <td>{{ formatCurrency(loan.remaining_amount) }}</td>
            <td>
              <n-space>
                <n-button 
                  size="small" 
                  type="primary"
                  :disabled="!loan.fixed_payment"
                  @click="payDueAmount(loan)"
                >
                  Pay Due Amount ({{ formatCurrency(loan.fixed_payment) }})
                </n-button>
                <n-button size="small" @click="editLoan(loan)">Edit</n-button>
                <n-button size="small" type="error" @click="deleteLoan(loan.id)">Delete</n-button>
              </n-space>
            </td>
          </tr>
        </tbody>
      </n-table>
      
      <n-button @click="showLoanForm = true">Add Loan</n-button>

      <!-- Loan Form Modal -->
      <n-modal 
        v-model:show="showLoanForm"
        preset="card"
        :mask-closable="true" 
        :title="isEditing ? 'Edit Loan' : 'Add Loan'"
        style="width: 60%; max-width: 600px"
      >
        <n-form 
          ref="formRef" 
          :model="formLoan" 
          label-placement="left"
          label-width="120"
        >
          <n-form-item label="Name" path="name">
            <n-input v-model:value="formLoan.name" />
          </n-form-item>
          <n-form-item label="Type" path="type">
            <n-select v-model:value="formLoan.type" :options="loanTypes" />
          </n-form-item>
          <n-form-item label="Amount" path="amount">
            <n-input-number 
              v-model:value="formLoan.amount" 
              :min="0"
              :precision="2"
              style="width: 100%"
            />
          </n-form-item>
          <n-form-item label="Interest Rate" path="interest_rate">
            <n-input-number 
              v-model:value="formLoan.interest_rate" 
              :min="0"
              :max="100"
              :precision="2"
              style="width: 100%"
            />
          </n-form-item>
          <n-form-item label="Start Date" path="start_date">
            <n-date-picker 
              v-model:value="formLoan.start_date" 
              type="date"
            />
          </n-form-item>
          <n-form-item label="End Date" path="end_date">
            <n-date-picker 
              v-model:value="formLoan.end_date" 
              type="date"
            />
          </n-form-item>
          <n-form-item label="Fixed Payment" path="fixed_payment">
            <n-input-number 
              v-model:value="formLoan.fixed_payment" 
              :min="0"
              :precision="2"
              style="width: 100%"
            />
          </n-form-item>
          <div style="text-align: right">
            <n-button type="primary" @click="submitLoanForm">
              {{ isEditing ? 'Update' : 'Add' }}
            </n-button>
          </div>
        </n-form>
      </n-modal>

      <!-- Payment Modal -->
      <n-modal 
        v-model:show="showPaymentForm"
        preset="card"
        :mask-closable="true" 
        title="Add Payment"
        style="width: 60%; max-width: 600px"
      >
        <n-form 
          ref="paymentFormRef" 
          :model="formPayment" 
          label-placement="left"
          label-width="120"
        >
          <n-form-item label="Amount" path="amount">
            <n-input-number 
              v-model:value="formPayment.amount" 
              :min="0"
              :precision="2"
              style="width: 100%"
            />
          </n-form-item>
          <n-form-item label="Date" path="date">
            <n-date-picker 
              v-model:value="formPayment.date" 
              type="date"
            />
          </n-form-item>
          <div style="text-align: right">
            <n-button type="primary" @click="submitPaymentForm">
              Add Payment
            </n-button>
          </div>
        </n-form>
      </n-modal>
    </n-space>
  </n-card>
</template>

<script setup>
import { useLoanStore } from '../stores/loanStore';
import { onMounted, computed, ref } from 'vue';

const loanStore = useLoanStore();
const loans = computed(() => loanStore.loans);
const showLoanForm = ref(false);
const showPaymentForm = ref(false);
const isEditing = ref(false);
const editingId = ref(null);
const selectedLoanId = ref(null);

const loanTypes = [
  { label: 'Personal', value: 'Personal' },
  { label: 'Building', value: 'Building' },
  { label: 'Car', value: 'Car' },
  { label: 'Other', value: 'Other' }
];

const formLoan = ref({
  name: '',
  type: null,
  amount: 0,
  interest_rate: 0,
  start_date: null,
  end_date: null,
  fixed_payment: 0
});

const formPayment = ref({
  amount: 0,
  date: null
});

onMounted(() => {
  loanStore.fetchLoans();
});

const formatDate = (date) => {
  return new Date(date).toLocaleDateString();
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('default', {
    style: 'currency',
    currency: 'EUR'
  }).format(value);
};

const formatPercent = (value) => {
  return value.toFixed(2) + '%';
};

const getLoanTypeStyle = (type) => {
  switch (type) {
    case 'Personal': return 'info';
    case 'Building': return 'success';
    case 'Car': return 'warning';
    default: return 'default';
  }
};

const getStatusStyle = (status) => {
  switch (status) {
    case 'Active': return 'info';
    case 'Paid': return 'success';
    case 'Defaulted': return 'error';
    default: return 'default';
  }
};

const resetLoanForm = () => {
  formLoan.value = {
    name: '',
    type: null,
    amount: 0,
    interest_rate: 0,
    start_date: null,
    end_date: null,
    fixed_payment: 0
  };
  isEditing.value = false;
  editingId.value = null;
  showLoanForm.value = false;
};

const resetPaymentForm = () => {
  formPayment.value = {
    amount: 0,
    date: null
  };
  selectedLoanId.value = null;
  showPaymentForm.value = false;
};

const deleteLoan = async (id) => {
  try {
    await loanStore.deleteLoan(id);
  } catch (error) {
    console.error('Error deleting loan:', error);
  }
};

const editLoan = (loan) => {
  isEditing.value = true;
  editingId.value = loan.id;
  formLoan.value = {
    ...loan,
    start_date: loan.start_date ? new Date(loan.start_date).getTime() : null,
    end_date: loan.end_date ? new Date(loan.end_date).getTime() : null
  };
  showLoanForm.value = true;
};

const submitLoanForm = async () => {
  try {
    if (isEditing.value) {
      await loanStore.updateLoan(editingId.value, formLoan.value);
    } else {
      await loanStore.addLoan(formLoan.value);
    }
    resetLoanForm();
  } catch (error) {
    console.error('Error submitting loan form:', error);
  }
};

const submitPaymentForm = async () => {
  try {
    await loanStore.addPayment(selectedLoanId.value, formPayment.value);
    resetPaymentForm();
  } catch (error) {
    console.error('Error submitting payment:', error);
  }
};

const payDueAmount = async (loan) => {
  if (!loan.fixed_payment) return;
  
  try {
    await loanStore.addPayment(loan.id, {
      amount: loan.fixed_payment/100,
      date: new Date()
    });
  } catch (error) {
    console.error('Error making payment:', error);
  }
};
</script>